import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { UserPlus, LogIn } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Uyelik({ setUser }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  
  const [registerData, setRegisterData] = useState({
    email: '',
    phone: '',
    password: '',
    nickname: ''
  });
  
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(registerData)
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('chatSince', new Date().toISOString());
        setUser(data.user);
        toast.success('Kayıt başarılı! Hoş geldiniz!');
        navigate('/');
      } else {
        toast.error(data.detail || 'Kayıt başarısız');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginData)
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('chatSince', new Date().toISOString());
        setUser(data.user);
        toast.success('Giriş başarılı!');
        navigate('/');
      } else {
        toast.error(data.detail || 'Giriş başarısız');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white py-12">
      <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 fade-in">
          <h1 className="text-4xl font-bold text-amber-900 mb-4" style={{ fontFamily: 'Merriweather, serif' }}>
            Üyelik
          </h1>
          <p className="text-lg text-amber-700">
            Derneğimize katılın veya giriş yapın
          </p>
        </div>

        <Tabs defaultValue="register" className="w-full">
          <TabsList className="grid w-full grid-cols-2 bg-amber-100">
            <TabsTrigger value="register" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white">
              Kayıt Ol
            </TabsTrigger>
            <TabsTrigger value="login" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white">
              Giriş Yap
            </TabsTrigger>
          </TabsList>

          <TabsContent value="register">
            <Card className="border-2 border-amber-200">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-amber-600 to-orange-600 rounded-full flex items-center justify-center">
                    <UserPlus className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-amber-900">Yeni Üyelik</CardTitle>
                    <CardDescription>Derneğimize üye olun</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleRegister} className="space-y-4">
                  <div>
                    <Label htmlFor="register-email" className="text-amber-900">E-posta</Label>
                    <Input
                      id="register-email"
                      type="email"
                      placeholder="ornek@email.com"
                      value={registerData.email}
                      onChange={(e) => setRegisterData({...registerData, email: e.target.value})}
                      required
                      className="border-amber-300 focus:border-amber-500"
                      data-testid="register-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="register-phone" className="text-amber-900">Telefon</Label>
                    <Input
                      id="register-phone"
                      type="tel"
                      placeholder="555 123 45 67"
                      value={registerData.phone}
                      onChange={(e) => setRegisterData({...registerData, phone: e.target.value})}
                      required
                      className="border-amber-300 focus:border-amber-500"
                      data-testid="register-phone-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="register-nickname" className="text-amber-900">Kullanıcı Adı (Nickname)</Label>
                    <Input
                      id="register-nickname"
                      type="text"
                      placeholder="Sohbette görünecek isim"
                      value={registerData.nickname}
                      onChange={(e) => setRegisterData({...registerData, nickname: e.target.value})}
                      required
                      className="border-amber-300 focus:border-amber-500"
                      data-testid="register-nickname-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="register-password" className="text-amber-900">Şifre</Label>
                    <Input
                      id="register-password"
                      type="password"
                      placeholder="Güçlü bir şifre seçin"
                      value={registerData.password}
                      onChange={(e) => setRegisterData({...registerData, password: e.target.value})}
                      required
                      className="border-amber-300 focus:border-amber-500"
                      data-testid="register-password-input"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-amber-600 to-orange-600 text-white hover:from-amber-700 hover:to-orange-700"
                    disabled={loading}
                    data-testid="register-submit-btn"
                  >
                    {loading ? 'Kayıt yapılıyor...' : 'Kayıt Ol'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="login">
            <Card className="border-2 border-amber-200">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-amber-600 to-orange-600 rounded-full flex items-center justify-center">
                    <LogIn className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-amber-900">Üye Girişi</CardTitle>
                    <CardDescription>Hesabınıza giriş yapın</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <Label htmlFor="login-email" className="text-amber-900">E-posta</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="ornek@email.com"
                      value={loginData.email}
                      onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                      required
                      className="border-amber-300 focus:border-amber-500"
                      data-testid="login-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="login-password" className="text-amber-900">Şifre</Label>
                    <Input
                      id="login-password"
                      type="password"
                      placeholder="Şifrenizi girin"
                      value={loginData.password}
                      onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                      required
                      className="border-amber-300 focus:border-amber-500"
                      data-testid="login-password-input"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-amber-600 to-orange-600 text-white hover:from-amber-700 hover:to-orange-700"
                    disabled={loading}
                    data-testid="login-submit-btn"
                  >
                    {loading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
